/**
 * **A port of `legacy/frontend/src/screens/Onboarding.tsx`, whole but for its `:3`.** F-001 T5:
 * «Вступление» — the three cards an invite can stop at (`invalid`, `paused`, `pending`), the join
 * form it otherwise opens, the card for somebody with nothing to offer, and the create form.
 *
 * Their `:3` is one import that mixes the values `ApiError, api` with the types `AuthResponse`,
 * `Birthday` and `VoicePart`. The types are server shapes, so here they come from
 * `@contracts/index` (ADR-0011), and the values stay on `../api` — `BirthdayCard.tsx`'s split, and
 * the blank line between the two is ours as well. Their `:1-2` and their `:4` to the end are theirs
 * byte for byte, and `scripts/ported-partial.txt` holds both regions.
 *
 * **Two security findings land here as non-deviations**, both `FIX` in the legacy repository and
 * neither changed on this side: «Вступить» still sends `askWriteAccess`'s answer as
 * `allows_write_to_pm` in the join body (`FND-1008`), and no name input carries a `maxLength`
 * (`FND-1009`).
 *
 * **The `choirName === null` and button-less variants of the paused and pending cards are ported
 * as written** (`FND-1021`, `KEEP`): nothing in the app can draw either, and the build spec's AC-25
 * checks them by review against the old lines rather than by a test.
 */
import { useState } from 'react';

import type { AuthResponse, Birthday, VoicePart } from '@contracts/index';

import { ApiError, api } from '../api';
import BirthdayWheel from './BirthdayWheel';
import Dock from './Dock';
import PartSegments from './PartSegments';
import SexSegments from './SexSegments';
import { askWriteAccess } from '../telegram';

/**
 * The part, and only on the way in that makes somebody a regent.
 *
 * It stood on both forms until 2026-08-24, when the owner's decision made a
 * part the regent's record rather than a thing a person states about
 * themselves. Whoever fills in the other form is about to be the regent, so
 * that is him setting his own — the same rule, not an exception to it.
 *
 * It is the segments the rest of the app uses, not a `<select>` of its own: before 2026-08-21 this was the third of
 * three copies of one field and the only one drawn by the operating system, so
 * the first thing a new singer met was the one control in the app that does not
 * look like the app.
 */
function VoicePartField({
  value,
  onChange,
}: {
  value: VoicePart | null;
  onChange: (part: VoicePart) => void;
}) {
  return (
    <div className="field">
      <span>Партия</span>
      <PartSegments value={value} onPick={onChange} />
    </div>
  );
}

/**
 * The other field both ways in share, and the reason it is here rather than
 * only on «Профиль»: the door is the one place everybody passes. A field that
 * lives on a settings screen is a field half the choir never answers, and the
 * queue of sermons is built out of the answer.
 *
 * Unlike the part it is required — the button below stays dead until one of the
 * two is pressed. Nothing back-fills the people who joined before this existed,
 * so the only way the app stops having members it knows nothing about is by not
 * making new ones.
 */
function SexField({
  value,
  onChange,
}: {
  value: boolean | null;
  onChange: (isBrother: boolean) => void;
}) {
  return (
    <div className="field">
      <span>Пол</span>
      <SexSegments value={value} onPick={onChange} />
    </div>
  );
}

export type PendingInvite = {
  code: string;
  /** Null while the server has not said which choir it is — the heading then keeps its old wording. */
  choirName: string | null;
  /**
   * Where the person stands in that choir. Three of the four have nothing to
   * fill in: `paused` is somebody the regent took out of the choir for a while,
   * `pending` is somebody who has already knocked and is waiting, and `invalid`
   * is a code the server does not know — a link the regent has since changed.
   * `unknown` is the server not having answered at all, and then the form is
   * offered as it was before there was anything to ask.
   */
  standing: 'unknown' | 'stranger' | 'paused' | 'pending' | 'invalid';
};

type Props = {
  suggestedName: string;
  invite: PendingInvite | null;
  mayCreate: boolean;
  /**
   * Somewhere to go back to: null when the person has no choir of their own and
   * this screen is the whole app. Without it an invite that leads nowhere - a
   * choir they were removed from, or one they simply do not want to join - is a
   * dead end with no way out but restarting the app.
   */
  onDismissInvite: (() => void) | null;
  onAuthenticated: (auth: AuthResponse) => void;
};

export default function Onboarding({
  suggestedName,
  invite,
  mayCreate,
  onDismissInvite,
  onAuthenticated,
}: Props) {
  const [name, setName] = useState(suggestedName);
  const [choirName, setChoirName] = useState('');
  const [voicePart, setVoicePart] = useState<VoicePart | null>(null);
  const [birthday, setBirthday] = useState<Birthday | null>(null);
  const [isBrother, setIsBrother] = useState<boolean | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(action: () => Promise<AuthResponse>): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      onAuthenticated(await action());
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'Не получилось, попробуй ещё раз');
    } finally {
      setBusy(false);
    }
  }

  if (invite !== null) {
    // Nothing to fill in: the join would be refused, and being told so after
    // typing a name and picking a part is being told so twice as late.
    if (invite.standing === 'invalid') {
      return (
        <section className="refusal refusal--quiet">
          <p className="label label--tone">Ссылка недействительна</p>
          <p className="refusal-fact">Приглашение перевыпустили</p>
          <p className="refusal-note">
            Прежняя ссылка перестала работать. Попроси у регента новую — по ней всё откроется.
          </p>
          {onDismissInvite !== null && (
            <button className="btn btn--outline" onClick={onDismissInvite}>
              Открыть приложение
            </button>
          )}
        </section>
      );
    }

    // Said before the form rather than after it: being told this once a name
    // and a birthday have been filled in is being told twice as late.
    //
    // Neither is a refusal and neither is in the danger colour. Somebody in a
    // pause is coming back and the link is simply not what brings them; somebody
    // who has already knocked is waiting on a person, not on the app. What each
    // says is who the answer is with — the regent, both times.
    if (invite.standing === 'paused') {
      return (
        <section className="refusal refusal--quiet">
          <p className="label label--tone">Ты сейчас не в составе</p>
          <p className="refusal-fact">
            {invite.choirName === null
              ? 'Пауза от регента'
              : `Пауза от регента в «${invite.choirName}»`}
          </p>
          <p className="refusal-note">
            Ссылка обратно не вернёт: снять паузу может только регент. Отметки за прошлые спевки
            никуда не делись.
          </p>
          {onDismissInvite !== null && (
            <button className="btn btn--outline" onClick={onDismissInvite}>
              Открыть приложение
            </button>
          )}
        </section>
      );
    }

    if (invite.standing === 'pending') {
      return (
        <section className="refusal refusal--quiet">
          <p className="label label--tone">Заявка отправлена</p>
          <p className="refusal-fact">
            {invite.choirName === null ? 'Ждёшь регента' : `Ждёшь регента хора «${invite.choirName}»`}
          </p>
          <p className="refusal-note">
            Регент увидит её на экране хора. Пока не примет, хор не открыт.
          </p>
          {onDismissInvite !== null && (
            <button className="btn btn--outline" onClick={onDismissInvite}>
              Открыть приложение
            </button>
          )}
        </section>
      );
    }

    return (
      <>
        <section className="join card--toned card--toned--outline">
          <p className="label label--tone">Вступление по ссылке</p>
          {/* The name of the choir, once the server has said which one the code
              opens. It used to read «Вас пригласили в хор» and stop there: the
              person was asked to write themselves down without being told where.
              «в «Название»» and not «в хор «Название»»: choirs are called «Хор
              нашей церкви», so the longer wording says the word twice. */}
          <p className="join-fact">{invite.choirName ?? 'Хор'}</p>
          <label className="field">
            <span>Как тебя записать</span>
            <input value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          <p className="hint">
            Так тебя будет видеть регент в списке хора и в посещаемости. Лучше имя и фамилию: в
            хоре может оказаться вторая Анна.
          </p>
          {/* No part here since 2026-08-24: the regent hears the voice and
              writes it down, and until he does the roster says «ждёт партию».
              The sex stays, because that one is the person's own. */}
          <SexField value={isBrother} onChange={setIsBrother} />
          <BirthdayWheel value={birthday} onChange={setBirthday} />
        </section>

        {/* Pinned: the wheel made this form taller than the smallest phone, and
            the way in is not something to go looking for. */}
        <Dock>
          {failure !== null && <p className="failure">{failure}</p>}
          <button
            className="btn btn--primary"
            disabled={busy || name.trim().length === 0 || isBrother === null}
            onClick={() =>
              void submit(async () => {
                // Telegram is asked here rather than after the request, and
                // that is what a knock at the door costs: a link makes a
                // request since 2026-08-27, so there is no token afterwards
                // and `POST /auth/write-access` would have nothing to
                // authenticate. The permission travels with the join instead.
                // On screen the popup lands exactly where it always did —
                // immediately after «Вступить».
                const allowed = await askWriteAccess();
                return api.joinChoir(invite.code, name.trim(), birthday, isBrother, allowed);
              })
            }
          >
            Вступить
          </button>
          {onDismissInvite !== null && (
            <button className="btn btn--quiet" disabled={busy} onClick={onDismissInvite}>
              Не сейчас
            </button>
          )}
        </Dock>
      </>
    );
  }

  // With no invite and no right to create there is no form worth offering:
  // the server refuses, and by then the person has invented a choir name and
  // written themselves down.
  if (!mayCreate) {
    return (
      <section className="card">
        <h2>Нужна ссылка-приглашение</h2>
        {/* This said «ссылкой или QR-кодом» until 2026-09-04, and the second
            half was never true: the app has one QR and it is the check-in at a
            rehearsal — a regent hands out a link and nothing else. So somebody
            at the door was told to look for a code nobody was ever going to
            give them. */}
        <p className="hint">
          В хор добавляет регент — ссылкой. Попроси её и открой: приложение сразу предложит
          записаться.
        </p>
        {/* The second line is for somebody who did open a link and is standing
            here anyway. The bot remembers the invite, but only from the moment
            `/start` reaches it — and until «Запустить» is pressed in that chat
            it never does. */}
        <p className="hint">
          Уже открывал ссылку? Вернись в чат с ботом и нажми кнопку под его сообщением.
        </p>
      </section>
    );
  }

  return (
    <>
      <section className="card">
        <h2>Создать хор</h2>
        <p className="hint">Ты станешь регентом и сможешь пригласить остальных ссылкой.</p>
        <label className="field">
          <span>Название хора</span>
          <input
            value={choirName}
            onChange={(event) => setChoirName(event.target.value)}
            placeholder="Хор нашей церкви"
          />
        </label>
        <label className="field">
          <span>Как тебя записать</span>
          <input value={name} onChange={(event) => setName(event.target.value)} />
        </label>
        {/* The same sentence as when joining, minus the regent: the person
            filling this in is about to become one. */}
        <p className="hint">
          Так тебя будет видно в списке хора и в посещаемости. Лучше имя и фамилию: в хоре может
          оказаться вторая Анна.
        </p>
        {/* Asked here too, not only when joining. The regent sings, and the form
            used to send «не выбрана» on their behalf: their own row read «без
            партии», the by-voice-part counts were short by one, and the field to
            fix it lives on another screen entirely. */}
        <VoicePartField value={voicePart} onChange={setVoicePart} />
        {/* Required here for the same reason the part is asked here at all: the
            regent sings, he stands in the queue of sermons like everybody else,
            and this form is the only one he ever sees. */}
        <SexField value={isBrother} onChange={setIsBrother} />
        {/* The regent has a birthday too, and this is the only form he ever
            sees: asked here or the choir never learns it. */}
        <BirthdayWheel value={birthday} onChange={setBirthday} />
      </section>

      {/* Pinned, and this is the form that asked for it: on a 375×667 phone it
          stands 73px taller than the screen, so «Создать» was under the fold
          the moment the wheel replaced the two selects. */}
      <Dock>
        {failure !== null && <p className="failure">{failure}</p>}
        <button
          className="btn btn--primary"
          disabled={
            busy ||
            choirName.trim().length === 0 ||
            name.trim().length === 0 ||
            // Both required, and for the same reason as the sex: since
            // 2026-08-24 a part is never «не указана», and this is the one
            // form that still sets one.
            voicePart === null ||
            isBrother === null
          }
          onClick={() =>
            void submit(() =>
              api.createChoir(choirName.trim(), name.trim(), voicePart, birthday, isBrother),
            )
          }
        >
          Создать
        </button>
      </Dock>
    </>
  );
}
